import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { fuzzySearch } from "shared/lib/fuzzySearch"
import { BuildingType } from "shared/types/references/BuildingType"

import Fuzzy from "@leeoniya/ufuzzy"
import { ParseKeys } from "i18next"

import { BuildingReferenceSearchResult } from "../types/GlobalSearch"
import { fetchBuildingReferencesByBuildingTypes } from "./fetchBuildingReferencesByBuildingTypes"

interface UseBuildingReferencesByBuildingTypesProps {
  searchTerm?: string | null
}

const fuzzy = new Fuzzy()

export function useBuildingReferencesByBuildingTypes({
  searchTerm
}: UseBuildingReferencesByBuildingTypesProps): {
  results: BuildingReferenceSearchResult[]
  isLoading: boolean
} {
  const { t } = useTranslation()
  const [results, setResults] = useState<BuildingReferenceSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const buildingTypeLabels = useMemo(
    () =>
      Object.values(BuildingType).map(buildingType => ({
        buildingType,
        label: t(
          `BuildingReference.BuildingType.${buildingType}` as ParseKeys
        ) as string
      })),
    [t]
  )

  const matchedBuildingTypes = useMemo<BuildingType[]>(() => {
    if (!searchTerm || searchTerm.length < 2) return []
    return fuzzySearch(
      buildingTypeLabels,
      searchTerm,
      item => item.label,
      fuzzy
    )
      .filter(Boolean)
      .map(item => item.buildingType)
  }, [searchTerm, buildingTypeLabels])

  useEffect(() => {
    if (matchedBuildingTypes.length === 0) {
      setResults([])
      setIsLoading(false)
      return undefined
    }

    let isCancelled = false
    setIsLoading(true)

    fetchBuildingReferencesByBuildingTypes(matchedBuildingTypes)
      .then(objs => {
        if (!isCancelled)
          setResults(
            objs.map(obj => ({
              obj,
              id: obj.id(),
              title: obj.get("title"),
              matchInfo: {
                buildingTypes: Object.fromEntries(
                  obj
                    .get("buildingType")
                    .map(buildingType => [buildingType, { searchTerm }])
                )
              }
            }))
          )
      })
      .catch(() => {
        if (!isCancelled) setResults([])
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false)
      })

    return () => {
      isCancelled = true
    }
  }, [matchedBuildingTypes, searchTerm])

  return { results, isLoading }
}
